import { useEffect } from "react";

import { useStore } from "zustand";

import { authStore } from "@/auth";
import { createStatsigUser } from "@/features/statsig/utils/create-statsig-user";
import type { StatsigClient } from "@statsig/react-bindings";

/**
 * The one place the Statsig user follows auth. It re-initialises the client
 * only when the user ID or device ID actually differs from the client's user:
 * sign-in, sign-out, or a different account. `useMe` used to do this on every
 * consumer's `onCompleted`, which fired one `/v1/initialize` per component.
 *
 * A separate component so the store subscription re-renders only this, not
 * the provider and the app tree under it.
 */
export const StatsigUserSync = ({ client }: { client: StatsigClient }) => {
  const userId = useStore(authStore, state => state.userId) ?? undefined;
  const deviceId = useStore(authStore, state => state.deviceId);

  useEffect(() => {
    const { user } = client.getContext();

    if (user.userID === userId && user.customIDs?.stableID === deviceId) {
      return;
    }

    void client.updateUserAsync(createStatsigUser({ userId, deviceId }));
  }, [client, userId, deviceId]);

  return null;
};
